import { Spin, Typography, Row, Col, Flex, Checkbox, App, message as antdMessage, notification as antdNotification } from 'antd'
import { useState, useEffect } from 'react';
import { emailRules, phoneNumberRules } from '../../../helpers/helpers'
import { useSelector, useDispatch } from "react-redux";
import { kycHistory, getAadhaarAndPan, getConsentDigilocker, busKycHistory, busGetAadhaarAndPan, busGetConsentDigilocker, sendDirectorKycLink } from '../../../redux/slices/EnqMngtSlice';
import { TITLE_TXT, BTN_TXT } from '../../../constants/constants';
import CustomInput from '../../../components/CustomInput/Input';
import { useSearchParams } from 'react-router-dom';
import PrimaryButton from '../../../components/CustomButton/PrimaryButton';

const { Text } = Typography;

const DIGI_CONSENT_STORAGE_PREFIX = "digilockerConsent:";

const getDigiConsentStorageKey = (orgKey) =>
    orgKey ? `${DIGI_CONSENT_STORAGE_PREFIX}${orgKey}` : null;

const readStoredDigiConsent = (orgKey) => {
    try {
        const key = getDigiConsentStorageKey(orgKey);
        return Boolean(key && sessionStorage.getItem(key) === "1");
    } catch {
        return false;
    }
};

const persistDigiConsent = (orgKey) => {
    try {
        const key = getDigiConsentStorageKey(orgKey);
        if (key) sessionStorage.setItem(key, "1");
    } catch {
        // ignore storage failures (private mode, etc.)
    }
};

const clearStoredDigiConsent = (orgKey) => {
    try {
        const key = getDigiConsentStorageKey(orgKey);
        if (key) sessionStorage.removeItem(key);
    } catch {
        // ignore
    }
};

const KycContactDirector = ({ kycData, email, phoneNo, orgId, form, isReview, organizationId, token, dealCategoryId }) => {
    const dispatch = useDispatch()
    const [searchParams] = useSearchParams();
    const params = searchParams.get("status");
    const { loading, kycHistoryData, digilockerLoading, pendingKycData } =
        useSelector((state) => state.enqMngt);
    const aadhaarDetails =
        kycHistoryData?.kycDetails?.kycDirectorsAadhaarValue ||
        kycData?.kycDirectorsAadhaarValue;
    const panDetails =
        kycHistoryData?.kycDetails?.kycDirectorsPanValue ||
        kycData?.kycDirectorsPanValue;
    // DigiLocker and manual Approve both settle when Aadhaar + PAN are verified
    const isDirectorKycVerified = Boolean(
        aadhaarDetails?.isVerified && panDetails?.isVerified,
    );
    const orgKey = orgId || organizationId;
    const isDigiLockerReturnSuccess = params === "digilocker-consent-success";
    const [hasStoredDigiConsent, setHasStoredDigiConsent] = useState(() =>
        readStoredDigiConsent(orgKey),
    );
    // Consent stays locked after DigiLocker return even if the URL query is cleared
    const isDigiConsentCompleted = isDigiLockerReturnSuccess || hasStoredDigiConsent;
    const isDigiConsentLocked = isDigiConsentCompleted || isDirectorKycVerified;
    const canFetchAadhaarPan = isDigiConsentCompleted;
    const hasToken = Boolean(token || searchParams.get("token"));
    const [isDigiConsentGiven, setIsDigiConsentGiven] = useState(isDigiConsentCompleted);
    const [directorKycEmailLoading, setDirectorKycEmailLoading] = useState(false);

    let notification;
    let message;
    try {
        const app = App.useApp();
        notification = app?.notification;
        message = app?.message;
    } catch (e) {
        // Fallback for isolated test environments
    }

    const handleSendDirectorKyc = async () => {
        setDirectorKycEmailLoading(true);
        try {
            const res = await dispatch(sendDirectorKycLink({ orgId: orgId || organizationId, dealCategoryId }));

            const payload = res?.payload ?? res;
            const statusCode =
                payload?.statusCode ??
                payload?.status ??
                payload?.data?.statusCode ??
                payload?.data?.status ??
                res?.statusCode ??
                res?.status;

            const isSuccess =
                !res?.error &&
                res?.meta?.requestStatus !== "rejected" &&
                !payload?.error &&
                (Number(statusCode) === 200 ||
                 Number(statusCode) === 201 ||
                 statusCode === "SUCCESS" ||
                 payload?.data?.statusCode === "SUCCESS" ||
                 res?.meta?.requestStatus === "fulfilled" ||
                 Boolean(payload?.response && (!statusCode || Number(statusCode) < 400)) ||
                 payload?.success === true ||
                 res?.success === true);

            if (isSuccess) {
                const notificationMsg =
                    (typeof payload === "string" ? payload : null) ||
                    (typeof payload?.response === "string" ? payload.response : null) ||
                    "Director KYC verification email sent successfully to the registered business email address.";

                const toastMsg =
                    (typeof payload === "string" ? payload : null) ||
                    (typeof payload?.response === "string" ? payload.response : null) ||
                    "Director KYC verification email sent successfully.";

                if (notification?.success) {
                    notification.success({
                        message: "Success",
                        description: notificationMsg,
                    });
                } else if (message?.success) {
                    message.success(toastMsg);
                } else if (antdNotification?.success) {
                    antdNotification.success({
                        message: "Success",
                        description: notificationMsg,
                    });
                } else if (antdMessage?.success) {
                    antdMessage.success(toastMsg);
                }
            } else {
                const errMsg =
                    (typeof payload === "string" ? payload : null) ||
                    payload?.response?.data?.message ||
                    payload?.data?.message ||
                    (typeof payload?.error === "string" ? payload.error : null) ||
                    payload?.error?.message ||
                    (typeof payload?.message === "string" && payload.message !== "POST_FAILED" && payload.message !== "Inserted Successfully" ? payload.message : null) ||
                    (typeof payload?.response === "string" && !payload.response.toLowerCase().includes("success") ? payload.response : null) ||
                    res?.error?.message ||
                    (typeof res?.error === "string" ? res.error : null) ||
                    "Failed to send Director KYC verification email.";

                if (notification?.error) {
                    notification.error({ message: "Error", description: errMsg });
                } else if (message?.error) {
                    message.error(errMsg);
                } else if (antdNotification?.error) {
                    antdNotification.error({ message: "Error", description: errMsg });
                } else if (antdMessage?.error) {
                    antdMessage.error(errMsg);
                }
            }
        } catch (err) {
            const errDescription = err?.message || "An error occurred while sending Director KYC verification email.";
            if (notification?.error) {
                notification.error({ message: "Error", description: errDescription });
            } else if (message?.error) {
                message.error(errDescription);
            } else if (antdNotification?.error) {
                antdNotification.error({ message: "Error", description: errDescription });
            } else if (antdMessage?.error) {
                antdMessage.error(errDescription);
            }
        } finally {
            setDirectorKycEmailLoading(false);
        }
    };

    useEffect(() => {
        if (isDigiLockerReturnSuccess) {
            persistDigiConsent(orgKey);
            setHasStoredDigiConsent(true);
            setIsDigiConsentGiven(true);
        }
    }, [isDigiLockerReturnSuccess, orgKey]);

    useEffect(() => {
        if (isDirectorKycVerified) {
            clearStoredDigiConsent(orgKey);
        }
    }, [isDirectorKycVerified, orgKey]);

    useEffect(() => {
        const details = kycHistoryData?.kycDetails || kycData;
        const patch = {};

        if (details?.kycDirectorsAadhaarValue?.isVerified) {
            patch.dirAadhaar = details.kycDirectorsAadhaarValue?.docValue;
            patch.dirName =
                kycHistoryData?.directorName ||
                pendingKycData?.directorName ||
                form.getFieldValue("dirName");
        }

        if (details?.kycDirectorsPanValue?.isVerified) {
            patch.dirPan = details.kycDirectorsPanValue?.docValue;
        }

        if (Object.keys(patch).length) {
            form.setFieldsValue(patch);
        }
    }, [form, isDirectorKycVerified, kycHistoryData, kycData, pendingKycData]);

    const fetchAadhaarPan = async () => {
        let response;
        if (token) {
            response = await dispatch(busGetAadhaarAndPan({ orgId: organizationId, token, dealCategoryId }));
        } else {
            response = await dispatch(getAadhaarAndPan({ orgId, dealCategoryId }));
        }

        if (response?.payload?.statusCode === 201) {
            let res;
            if (token) {
                res = await dispatch(busKycHistory({ orgId: organizationId, token }));
            } else {
                res = await dispatch(kycHistory(orgId));
            }
            if (res?.payload?.directorName || res?.payload?.kycDetails) {
                form.setFieldsValue({
                    dirName: res?.payload?.directorName,
                    dirAadhaar: res?.payload?.kycDetails?.kycDirectorsAadhaarValue?.docValue,
                    dirPan: res?.payload?.kycDetails?.kycDirectorsPanValue?.docValue,
                });
            }
        }
    };

    const handleProceedDigi = async () => {
        let res;
        if (token) {
            res = await dispatch(busGetConsentDigilocker({ orgId: organizationId, token, dealCategoryId }));
        } else {
            res = await dispatch(getConsentDigilocker({ orgId, dealCategoryId }));
        }

        if (res?.payload.response) {
            window.location.href = res.payload.response;
        }
    }

    return (
        <Spin spinning={loading}>
            <Text className='kyc-subtitle'>{TITLE_TXT.CONTACT_DETS}</Text>
            <Row gutter={[24, 0]}>
                <Col xs={24} md={12}>
                    <CustomInput
                        label="Email Address"
                        name="email"
                        type="email"
                        isRequired={true}
                        placeholder="Enter Contact Email"
                        rules={emailRules("Contact")}
                        disabled
                    />
                </Col>
                <Col xs={24} md={12}>
                    <CustomInput
                        label="Mobile Number"
                        name="phoneNo"
                        isRequired={true}
                        placeholder="Enter Contact Phone Number"
                        rules={phoneNumberRules("Contact")}
                        disabled
                        prefix="+91"
                    />
                </Col>
            </Row>
            <Flex align='center' justify='space-between' wrap style={{ margin: '16px 0 8px 0' }}>
                <Text className='kyc-subtitle' style={{ margin: 0 }}>{TITLE_TXT.DIR_KYC}</Text>
                {!isDirectorKycVerified && !hasToken && (
                    <PrimaryButton
                        onClick={handleSendDirectorKyc}
                        loading={directorKycEmailLoading}
                        style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                    >
                        Send Director Verification Email
                    </PrimaryButton>
                )}
            </Flex>
            {isDirectorKycVerified ? (
                <Row gutter={[24, 0]}>
                    <Col xs={24} md={12}>
                        <CustomInput
                            label="Director Name"
                            name="dirName"
                            type="text"
                            isRequired={true}
                            disabled={isReview || isDirectorKycVerified}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <CustomInput
                            label="Director Aadhaar"
                            name="dirAadhaar"
                            type="text"
                            isRequired={true}
                            disabled={isReview || isDirectorKycVerified}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <CustomInput
                            label="Director PAN"
                            name="dirPan"
                            type="text"
                            isRequired={true}
                            disabled={isReview || isDirectorKycVerified}
                        />
                    </Col>
                </Row>
            ) : isReview ? (
                <Text type="secondary">Director KYC details are locked.</Text>
            ) : (
                <Flex align='center' gap={20} wrap>
                    <Checkbox
                        style={{ margin: '16px 0' }}
                        checked={isDigiConsentGiven}
                        disabled={isDigiConsentLocked}
                        onChange={(e) => {
                            if (!isDigiConsentLocked) {
                                setIsDigiConsentGiven(e.target.checked);
                            }
                        }}
                    >
                        I hereby give consent to fetch my KYC details via DigiLocker
                    </Checkbox>
                    <PrimaryButton
                        disabled={!isDigiConsentGiven || isDigiConsentLocked}
                        loading={digilockerLoading}
                        onClick={handleProceedDigi}
                    >
                        {BTN_TXT.PRO_WITH_DIGI}
                    </PrimaryButton>
                    <PrimaryButton disabled={!canFetchAadhaarPan} loading={digilockerLoading} onClick={fetchAadhaarPan}>
                        {BTN_TXT.FETCH_AADHAAR_PAN}
                    </PrimaryButton>
                </Flex >
            )}
        </Spin>
    )
}

export default KycContactDirector
